import { useCallback, useEffect, useRef, useState } from "react";
import type { PointerEvent as RPointerEvent } from "react";
import { Pipette } from "lucide-react";

/* منتقي ألوان مخصص بالكامل (بدل نافذة اللون الأصلية للنظام). */

interface HSV {
  h: number; // 0..360
  s: number; // 0..1
  v: number; // 0..1
}

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const RECENT_KEY = "sh_recent_colors";

const QUICK = [
  "#FFFFFF", "#F5F5F5", "#9CA3AF", "#374151", "#000000",
  "#EF4444", "#F97316", "#F59E0B", "#EAB308", "#84CC16",
  "#22C55E", "#14B8A6", "#06B6D4", "#3B82F6", "#6366F1",
  "#8B5CF6", "#D946EF", "#EC4899", "#F43F5E", "#B45309",
];

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

export function hexToHsv(hex: string): HSV {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex);
  if (!m) return { h: 0, s: 0, v: 0 };
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d > 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : d / max, v: max };
}

export function hsvToHex({ h, s, v }: HSV): string {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0, g = 0, b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const to = (f: number) => Math.round((f + m) * 255).toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
}

function loadRecent(): string[] {
  try {
    const arr = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    return Array.isArray(arr) ? arr.filter((x) => typeof x === "string" && HEX_RE.test(x)).slice(0, 10) : [];
  } catch {
    return [];
  }
}

function saveRecent(hex: string): string[] {
  const next = [hex.toLowerCase(), ...loadRecent().filter((c) => c.toLowerCase() !== hex.toLowerCase())].slice(0, 10);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* التخزين غير متاح - مو مشكلة */
  }
  return next;
}

/** سحب بمؤشر واحد (لمس/ماوس) داخل عنصر، يعيد إحداثيات 0..1. */
function dragProps(onMove: (x: number, y: number) => void, onEnd: () => void) {
  const apply = (e: RPointerEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    onMove(clamp01((e.clientX - r.left) / r.width), clamp01((e.clientY - r.top) / r.height));
  };
  return {
    onPointerDown: (e: RPointerEvent<HTMLDivElement>) => {
      e.currentTarget.setPointerCapture(e.pointerId);
      apply(e);
    },
    onPointerMove: (e: RPointerEvent<HTMLDivElement>) => {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) apply(e);
    },
    onPointerUp: (e: RPointerEvent<HTMLDivElement>) => {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
        onEnd();
      }
    },
  };
}

export function ColorPickerPanel({ value, onChange }: { value: string; onChange: (hex: string) => void }) {
  const safe = HEX_RE.test(value) ? value : "#ffffff";
  const [hsv, setHsv] = useState<HSV>(() => hexToHsv(safe));
  const [hexText, setHexText] = useState(safe.slice(1).toUpperCase());
  const [recent, setRecent] = useState<string[]>(loadRecent);
  const original = useRef(safe);
  const hsvRef = useRef(hsv);
  hsvRef.current = hsv;
  const hasEyeDropper = typeof window !== "undefined" && "EyeDropper" in window;

  // مزامنة عند تغيّر القيمة من الخارج (لون جاهز، قطّارة...) بدون ضياع درجة اللون.
  useEffect(() => {
    if (!HEX_RE.test(value)) return;
    if (hsvToHex(hsvRef.current).toLowerCase() !== value.toLowerCase()) {
      setHsv(hexToHsv(value));
    }
    setHexText(value.slice(1).toUpperCase());
  }, [value]);

  const emit = useCallback(
    (next: HSV) => {
      setHsv(next);
      const hex = hsvToHex(next);
      setHexText(hex.slice(1).toUpperCase());
      onChange(hex);
    },
    [onChange],
  );

  const commit = useCallback(() => setRecent(saveRecent(hsvToHex(hsvRef.current))), []);

  const setHex = (hex: string) => {
    setHsv(hexToHsv(hex));
    setHexText(hex.slice(1).toUpperCase());
    onChange(hex.toLowerCase());
    setRecent(saveRecent(hex));
  };

  const current = hsvToHex(hsv);
  const pureHue = hsvToHex({ h: hsv.h, s: 1, v: 1 });

  const svDrag = dragProps((x, y) => emit({ ...hsvRef.current, s: x, v: 1 - y }), commit);
  const hueDrag = dragProps((x) => emit({ ...hsvRef.current, h: x * 359.999 }), commit);

  const pickWithEyeDropper = async () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res = await new (window as any).EyeDropper().open();
      if (res?.sRGBHex && HEX_RE.test(res.sRGBHex)) setHex(res.sRGBHex);
    } catch {
      /* المستخدم ألغى */
    }
  };

  return (
    <div className="flex flex-col gap-4 select-none" dir="ltr">
      {/* مربع التشبّع والسطوع */}
      <div
        {...svDrag}
        className="relative w-full rounded-2xl overflow-hidden touch-none cursor-crosshair border border-white/10"
        style={{ height: 190, backgroundColor: pureHue }}
        role="slider"
        aria-label="التشبّع والسطوع"
        aria-valuetext={current}
      >
        <div className="absolute inset-0" style={{ background: "linear-gradient(to right, #fff, rgba(255,255,255,0))" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #000, rgba(0,0,0,0))" }} />
        <span
          className="absolute w-[22px] h-[22px] rounded-full border-[3px] border-white pointer-events-none"
          style={{
            left: `${hsv.s * 100}%`,
            top: `${(1 - hsv.v) * 100}%`,
            transform: "translate(-50%, -50%)",
            backgroundColor: current,
            boxShadow: "0 0 0 1px rgba(0,0,0,.45), 0 3px 8px rgba(0,0,0,.5)",
          }}
        />
      </div>

      {/* شريط درجة اللون */}
      <div
        {...hueDrag}
        className="relative w-full h-4 rounded-full touch-none cursor-pointer border border-white/10"
        style={{ background: "linear-gradient(to right,#f00,#ff0 17%,#0f0 33%,#0ff 50%,#00f 67%,#f0f 83%,#f00)" }}
        role="slider"
        aria-label="درجة اللون"
      >
        <span
          className="absolute top-1/2 w-6 h-6 rounded-full border-[3px] border-white pointer-events-none"
          style={{
            left: `${(hsv.h / 360) * 100}%`,
            transform: "translate(-50%, -50%)",
            backgroundColor: pureHue,
            boxShadow: "0 0 0 1px rgba(0,0,0,.45), 0 3px 8px rgba(0,0,0,.5)",
          }}
        />
      </div>

      {/* معاينة + HEX + قطّارة */}
      <div className="flex items-center gap-3">
        <div className="flex w-14 h-14 rounded-2xl overflow-hidden border-2 border-white/20 shrink-0" title="القديم | الجديد">
          <button
            type="button"
            onClick={() => HEX_RE.test(original.current) && setHex(original.current)}
            className="flex-1 h-full"
            style={{ backgroundColor: original.current }}
            aria-label="رجوع للون الأصلي"
          />
          <div className="flex-1 h-full" style={{ backgroundColor: current }} />
        </div>

        <div className="flex-1 min-w-0 flex items-center gap-2 glass-pill rounded-xl px-3 py-2.5">
          <span className="text-tg-hint text-sm shrink-0">#</span>
          <input
            type="text"
            inputMode="text"
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            value={hexText}
            maxLength={6}
            placeholder="RRGGBB"
            onChange={(e) => {
              const digits = e.target.value.replace(/[^0-9a-fA-F]/g, "").slice(0, 6).toUpperCase();
              setHexText(digits);
              if (digits.length === 6) {
                setHsv(hexToHsv(`#${digits}`));
                onChange(`#${digits.toLowerCase()}`);
              }
            }}
            onBlur={() => {
              if (hexText.length === 6) setRecent(saveRecent(`#${hexText}`));
              else setHexText(current.slice(1).toUpperCase());
            }}
            className="flex-1 min-w-0 bg-transparent outline-none text-base font-mono tracking-wider uppercase"
            aria-label="كود اللون HEX"
          />
        </div>

        {hasEyeDropper && (
          <button
            type="button"
            onClick={pickWithEyeDropper}
            className="w-11 h-11 rounded-xl glass-pill flex items-center justify-center shrink-0"
            aria-label="قطّارة"
          >
            <Pipette size={18} />
          </button>
        )}
      </div>

      {recent.length > 0 && (
        <div>
          <p className="text-xs text-tg-hint mb-2" dir="rtl">الأخيرة</p>
          <div className="flex flex-wrap gap-2">
            {recent.map((c) => (
              <Swatch key={c} hex={c} active={c.toLowerCase() === current.toLowerCase()} onPick={setHex} />
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="text-xs text-tg-hint mb-2" dir="rtl">ألوان سريعة</p>
        <div className="grid grid-cols-10 gap-1.5">
          {QUICK.map((c) => (
            <Swatch key={c} hex={c} active={c.toLowerCase() === current.toLowerCase()} onPick={setHex} fill />
          ))}
        </div>
      </div>
    </div>
  );
}

function Swatch({ hex, active, onPick, fill }: { hex: string; active: boolean; onPick: (h: string) => void; fill?: boolean }) {
  return (
    <button
      type="button"
      onClick={() => onPick(hex)}
      aria-label={hex}
      className={`${fill ? "aspect-square w-full" : "w-8 h-8"} rounded-full border-2 transition-transform active:scale-90`}
      style={{
        backgroundColor: hex,
        borderColor: active ? "var(--tg-theme-button-color, #5288c1)" : "rgba(255,255,255,.18)",
      }}
    />
  );
}
